import { useFetchSpanStatus } from 'client/hooks/span/useSpanStatus';
import { LibraryAudio } from 'client/modules/AnchorAPI/fetchAudioLibrary';
import { Audio } from 'client/modules/AnchorAPI/v3/episodes';
import { WaveformAudioInfo } from 'client/screens/EpisodeScreen/context';
import { useFetchPodcastMetadata } from 'hooks/podcast/usePodcastMetadata';
import { getQueryStatus } from 'hooks/utils/getQueryStatus';
import { EpisodeAudios } from 'client/modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';
import { useFetchEpisodeOverview } from '../episodes/useFetchEpisodeOverview';

export const USE_FETCH_EPISODE_AUDIO_KEY = 'useAdInsertion';
type AdInsertionAudioType = WaveformAudioInfo &
  Pick<Audio, 'type' | 'duration'>;

type AdInsertionParams = {
  webEpisodeId?: string;
  libraryAudios?: LibraryAudio[];
};

/**
 * this hook will determine if a user can use the waveform UI to insert ads:
 * - is a SPAN user OR Studio creator
 * - episode has a single original audio (e.g. not a transition)
 *
 * the episode audio we use for the ad insertion can come from different places
 * (in order of preference):
 *
 * 1. libraryAudios - this is passed down as an argument to this hook. currently,
 *    this is used when a user is modifying their episode from the episode builder
 *    and are changing the episode segments in the staging area BEFORE the episode
 *    is actually saved. Because these are unsaved changes to the episode, we can't
 *    fetch for the audio from the server since the changes are happening on the
 *    client only
 * 2. audio segments saved to the episode - this would be the "normal" audio one
 *    could save to an episode
 */
export function useAdInsertion({
  webEpisodeId,
  libraryAudios,
}: AdInsertionParams) {
  const {
    status: statusFetchSpanStatus,
    isSPANUser,
    isError: isErrorFetchSpanStatus,
    refetch: refetchSpanStatus,
  } = useFetchSpanStatus();

  const {
    data: podcastMetadata,
    status: statusFetchPodcastMetadata,
    isError: isErrorFetchPodcastMetadata,
    refetch: refetchPodcastMetadata,
  } = useFetchPodcastMetadata();

  const isStudioCreator = Boolean(podcastMetadata?.isStudioCreator);
  const isWaveformAdInsertionUser = isSPANUser || isStudioCreator;

  const {
    episodeOverviewData,
    status: statusFetchEpisodeOverview,
    refetch: refetchEpisodeOverview,
  } = useFetchEpisodeOverview({
    webEpisodeId,
  });

  const episodeAudios = getEpisodeAudios({
    libraryAudios,
    audios: episodeOverviewData?.episodeAudios,
  });
  const isWaveformAdInsertionEpisode = getIsWaveformAdInsertionEpisode({
    audios: episodeAudios,
  });
  const { duration, audioId, url, caption } = episodeAudios[0] || {};

  // handle refetching for each failing query separately
  const handleRefetch = () => {
    // if the useFetchSpanStatus query fails
    if (isErrorFetchSpanStatus) refetchSpanStatus();
    // if the useFetchPodcastMetadata query fails
    if (isErrorFetchPodcastMetadata) refetchPodcastMetadata();
    // if the fetchEpisodeOverview query fails
    if (statusFetchEpisodeOverview === 'error') refetchEpisodeOverview();
  };
  return {
    isWaveformAdInsertionEpisode,
    isWaveformAdInsertionUser,
    duration,
    audioId,
    url,
    caption,
    handleRefetch,
    status: getQueryStatus([
      statusFetchEpisodeOverview,
      statusFetchPodcastMetadata,
      statusFetchSpanStatus,
    ]),
  };
}

/**
 * returns a new array of audios derived from audio/video fetched from one of a few places:
 * - audio library via episode builder's staging area
 * - directly fetching the episode audio (via the useQuery in the `useAdInsertion` hook)
 * - video for video podcasters
 */
function getEpisodeAudios({
  libraryAudios,
  audios,
}: {
  libraryAudios?: LibraryAudio[];
  audios?: EpisodeAudios[];
}): AdInsertionAudioType[] {
  if (libraryAudios?.length) {
    return libraryAudios.map(libraryAudio => {
      const { duration, audioId, caption, url, type } = libraryAudio;
      return { duration, audioId, caption, url: url || undefined, type };
    });
  }
  if (audios?.length) {
    return audios.map(audio => {
      const { duration, webAudioId, caption, url, type } = audio;
      return {
        duration,
        audioId: webAudioId,
        caption,
        url,
        type,
      };
    });
  }
  return [];
}

function getIsWaveformAdInsertionEpisode({
  audios,
}: {
  audios: AdInsertionAudioType[];
}) {
  // if no audio or more than one audio, user can't use waveform
  if (audios.length === 0 || audios.length > 1) return false;

  // if audio is user generated, user can use waveform
  if (
    !['ad', 'interlude', 'music', 'shoutout', 'callin'].includes(audios[0].type)
  )
    return true;

  // fallback to false
  return false;
}
